import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { Shield, Lock } from 'lucide-react';

interface SuperUserRouteProps {
  children: React.ReactNode;
}

export const SuperUserRoute: React.FC<SuperUserRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isCheckingSuperuser, setIsCheckingSuperuser] = useState(true);
  const [isSuperuser, setIsSuperuser] = useState(false);

  useEffect(() => {
    const checkSuperuserStatus = async () => {
      if (!isAuthenticated || !user) {
        setIsCheckingSuperuser(false);
        return;
      }

      try {
        // Check user object from context first
        if (user.is_superuser) {
          setIsSuperuser(true);
          setIsCheckingSuperuser(false);
          return;
        }

        // Fallback: Check API if not in user object
        const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
        if (!token) {
          setIsCheckingSuperuser(false);
          return;
        }

        const { API_ENDPOINTS } = await import('../config/api');
        const authUserEndpoint = API_ENDPOINTS.AUTH?.USER || '/api/auth/user/';
        const response = await fetch(authUserEndpoint, {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const userData = await response.json();
          setIsSuperuser(userData.is_superuser || false);
        }
      } catch (error) {
        console.error('Superuser check failed:', error);
        setIsSuperuser(false);
      } finally {
        setIsCheckingSuperuser(false);
      }
    };

    if (!isLoading) {
      checkSuperuserStatus();
    }
  }, [isAuthenticated, user, isLoading]);

  // Show loading while checking authentication
  if (isLoading || isCheckingSuperuser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Checking permissions...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <>
        <Helmet>
          <title>Access Denied - Superuser Required | MotivaMatch</title>
          <meta name="description" content="This page requires superuser access. Please sign in with a superuser account." />
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-purple-50 flex items-center justify-center px-4">
          <div className="max-w-xl w-full bg-white rounded-3xl shadow-xl border border-orange-100 p-8 md:p-10 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-purple-600 text-white mb-6">
              <Lock className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Sign in required
            </h2>
            <p className="text-gray-600 mb-6">
              This page requires you to be signed in. Please log in to continue.
            </p>
            <button
              onClick={() =>
                navigate('/auth', { state: { from: location }, replace: true })
              }
              className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-orange-500 to-purple-600 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Go to Login
            </button>
          </div>
        </div>
      </>
    );
  }

  // Show access denied if not superuser
  if (!isSuperuser) {
    return (
      <>
        <Helmet>
          <title>Access Denied - Superuser Required | MotivaMatch</title>
          <meta name="description" content="This page requires superuser access. You do not have permission to view this content." />
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-purple-50 flex items-center justify-center px-4">
          <div className="max-w-xl w-full bg-white rounded-3xl shadow-xl border border-red-100 p-8 md:p-10 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-red-500 to-orange-600 text-white mb-6">
              <Shield className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Access Denied
            </h2>
            <p className="text-gray-600 mb-2">
              This page requires <strong>superuser</strong> access.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              You do not have permission to view this content. Please contact an administrator if you believe this is an error.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center justify-center px-6 py-3 bg-gray-600 text-white font-semibold rounded-xl hover:bg-gray-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </>
    );
  }

  // User is authenticated and is superuser - show content
  return <>{children}</>;
};

